import { t as uiText } from '../../i18n';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { storage, db, auth } from '../../lib/firebase';
import { invalidateAvatarCache } from './userImageUtils';

export interface UpdateUserProfileParams {
  userId: string;
  displayName: string;
  imageFile: File | null;
  currentPhotoPath?: string | null;
  notificationEmail?: string | null;
  onProgress?: (progress: number) => void;
}

export const updateUserProfile = async ({
  userId,
  displayName,
  imageFile,
  currentPhotoPath,
  notificationEmail,
  onProgress
}: UpdateUserProfileParams): Promise<{ photoPath: string | null; displayName: string }> => {
  try {
    onProgress?.(10);

    let newPhotoPath: string | null = currentPhotoPath || null;

    // 画像ファイルの処理
    if (imageFile) {
      // 新しい画像をアップロード
      const timestamp = Date.now();
      const fileName = `userIcons/${userId}/${timestamp}_${imageFile.name}`;
      const storageRef = ref(storage, fileName);

      onProgress?.(30);

      // 画像をアップロード
      const snapshot = await uploadBytes(storageRef, imageFile);
      onProgress?.(70);

      // パスを保存（URLではなく）
      newPhotoPath = fileName;

      // 既存の画像がある場合は削除
      if (currentPhotoPath && currentPhotoPath !== newPhotoPath) {
        try {
          const oldImageRef = ref(storage, currentPhotoPath);
          await deleteObject(oldImageRef);
        } catch (error) {
          console.warn('🔄 updateUserProfile: 既存画像の削除に失敗（既に存在しない可能性）:', error);
        }
      }
    } else if (imageFile === null && currentPhotoPath) {
      // 画像削除が明示的に指定された場合
      try {
        const oldImageRef = ref(storage, currentPhotoPath);
        await deleteObject(oldImageRef);
        newPhotoPath = null;
      } catch (error) {
        console.warn('🔄 updateUserProfile: 既存画像の削除に失敗（既に存在しない可能性）:', error);
      }
    }

    onProgress?.(80);

    // Firebase Auth のプロフィールを更新（URL生成）
    const currentUser = auth ? auth.currentUser : null;
    if (currentUser && currentUser.uid === userId) {
      let photoURL: string | null = null;
      if (newPhotoPath) {
        const storageRef = ref(storage, newPhotoPath);
        photoURL = await getDownloadURL(storageRef);
      }
      
      await updateProfile(currentUser, {
        displayName,
        photoURL
      });
    }

    onProgress?.(90);

    // Firestore の users コレクションを更新（パス保存）
    const userRef = doc(db, 'users', userId);
    const trimmedNotificationEmail = notificationEmail?.trim() || null;
    await updateDoc(userRef, {
      displayName,
      photoPath: newPhotoPath,
      notificationEmail: trimmedNotificationEmail,
      updatedAt: serverTimestamp()
    });

    invalidateAvatarCache(userId);

    onProgress?.(100);

    return {
      photoPath: newPhotoPath,
      displayName
    };
  } catch (error) {
    console.error('🔄 updateUserProfile: エラーが発生しました:', error);
    throw new Error(uiText("Failed to update user profile"));
  }
};
